import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { ModuleShell } from "@/components/intel/ModuleShell";
import { AppLayout } from "@/components/intel/Layout";
import { useInvestigations } from "@/lib/intel/store";
import { askAnalyst } from "@/lib/intel/feeds.functions";
import { getPaddleEnvironment } from "@/lib/paddle";
import { Bot, Send, Brain } from "lucide-react";

export const Route = createFileRoute("/assistant")({
  head: () => ({ meta: [{ title: "AI Analyst · CARIO INTEL" }] }),
  component: AssistantPage,
});

function AssistantPage() {
  const { state, addNote } = useInvestigations();
  const caseId = state.cases[0]?.id;
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const ask = useServerFn(askAnalyst);
  const [messages, setMessages] = useState<{ role: "user" | "assistant"; content: string }[]>([
    { role: "assistant", content: "CARIO-AGENT online (Grok 4.3 with Live Web + X search via xAI). Ask me to synthesize findings, draft hypotheses, or correlate evidence. Strict LEGAL OSINT scope." },
  ]);
  const send = async () => {
    const text = input.trim();
    if (!text || busy) return;
    const next = [...messages, { role: "user" as const, content: text }];
    setMessages(next);
    setInput("");
    setErr(null);
    setBusy(true);
    try {
      const { reply } = await ask({
        data: {
          messages: next.map((m) => ({ role: m.role, content: m.content })),
          environment: getPaddleEnvironment(),
        },
      });
      setMessages((m) => [...m, { role: "assistant", content: reply }]);
    } catch (e) {
      setErr(e instanceof Error ? e.message : "AI request failed");
    } finally {
      setBusy(false);
    }
  };
  return (
    <AppLayout>
    <ModuleShell eyebrow="AI Analyst" title="CARIO-AGENT analytical copilot" description="Interactive hypothesis generation and case synthesis grounded in the active evidence store.">
      <div className="glass rounded-xl p-4">
        <div className="flex items-center gap-2 border-b border-panel-border pb-3"><Bot className="h-5 w-5 text-cyan" /><span className="font-mono text-[10px] uppercase tracking-[0.3em] text-cyan">context · {caseId ?? "no case"}</span></div>
        <div className="mt-4 min-h-[430px] space-y-3">
          {messages.map((m, i) => <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}><div className={`max-w-3xl whitespace-pre-wrap rounded-md px-3 py-2 text-sm leading-relaxed ${m.role === "user" ? "bg-cyan/15 ring-1 ring-cyan/30" : "border border-panel-border bg-background/40"}`}>{m.content}</div></div>)}
          {busy && <div className="flex justify-start"><div className="rounded-md border border-panel-border bg-background/40 px-3 py-2 text-sm text-muted-foreground animate-pulse">CARIO-AGENT analyzing…</div></div>}
          {err && <div className="rounded-md border border-danger/40 bg-danger/10 p-3 text-sm text-danger">{err}</div>}
        </div>
        <div className="mt-4 flex flex-wrap gap-2">
          {['Summarize active case','Generate risk hypothesis','Draft next steps','Find contradictions'].map((p) => <button key={p} onClick={() => setInput(p)} className="rounded-full border border-panel-border bg-background/40 px-3 py-1 font-mono text-[10px] text-muted-foreground hover:text-foreground"><Brain className="mr-1 inline h-3 w-3" />{p}</button>)}
        </div>
        <div className="mt-3 flex gap-2 rounded-md border border-panel-border bg-background/40 p-2"><input value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()} placeholder="Ask for an analytical synthesis…" className="flex-1 bg-transparent text-sm outline-none" disabled={busy} /><button onClick={send} disabled={busy} className="grid h-9 w-9 place-items-center rounded-md bg-primary text-primary-foreground disabled:opacity-50"><Send className="h-4 w-4" /></button><button disabled={!caseId || !messages.at(-1)?.content} onClick={() => caseId && addNote(caseId, messages.at(-1)?.content ?? "", true)} className="rounded-md border border-panel-border bg-panel/40 px-3 text-xs disabled:opacity-40">Pin note</button></div>
      </div>
    </ModuleShell>
    </AppLayout>
  );
}